import { Trans } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import {
  LoadingButton,
} from '@mui/lab';
import {
  Grid,
  Alert,
  Stack,
  Table,
  Button,
  Dialog,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogContent,
  TablePagination,
  CircularProgress,
  FormControlLabel
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { IJob, AddToJobNote } from 'src/services/jobs/jobs.types';
import { generateAddCandidateToJobError } from 'src/services/jobs/jobs.utils';
import { IOrgCandidate } from 'src/services/candidates/candidates.types';
import { hasProfileContent } from 'src/services/candidates/candidates.utils';
import { ApplicationStage } from 'src/services/applications/applications.types';
import { useLazyGetOrganizationCandidateQuery } from 'src/services/candidates/candidates.service';
import {
  useGetJobByIdQuery,
  useGetOrganisationJobsQuery,
  useAddCandidateToJobMutation,
} from 'src/services/jobs/jobs.service';

import Iconify from 'src/components/iconify';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import BuildProfilePrompt from './build-profile-prompt';
import AddToJobNoteField from './add-to-job-note-field';

type Props = {
  open: boolean;
  jobId?: string;
  onClose: VoidFunction;
  candidates: string[];
  /** Called once the run finishes, with the candidates that were added and the stage they went to. */
  onAdded?: (candidateIds: string[], stage: ApplicationStage) => void;
};

type View = 'selection' | 'processing' | 'results';

type Result = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: 'success' | 'error';
  error?: string;
};

const ALLOWED_STAGES = [ApplicationStage.APPLIED, ApplicationStage.SCREENING];

export default function BulkAddCandidatesJobModal({
  open,
  jobId,
  onClose,
  candidates,
  onAdded,
}: Props) {

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const tenantType = useOrgTenant();
  const { canAccess } = useFeatures();

  const [view, setView] = useState<View>('selection');
  const [error, setError] = useState<string | null>(null);
  const [jobSearchQuery, setJobSearchQuery] = useState<string>('');
  const [defaultStage, setDefaultStage] = useState<ApplicationStage>(
    ApplicationStage.APPLIED
  );
  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [createInvite, setCreateInvite] = useState<boolean>(true);
  const [note, setNote] = useState<AddToJobNote | undefined>(undefined);
  const [progress, setProgress] = useState<number>(0);
  const [results, setResults] = useState<Result[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);


  const { currentData: jobs } = useGetOrganisationJobsQuery({
    search_query: jobSearchQuery,
    page: 1,
    per_page: 5,
  });

  const { currentData: prefilledJob } = useGetJobByIdQuery(jobId!, {
    skip: !jobId,
  });

  const [addCandidateToJob, { isLoading: isSubmitting }] = useAddCandidateToJobMutation();
  const [getCandidateById] = useLazyGetOrganizationCandidateQuery();

  useEffect(() => {
    if (open && prefilledJob) {
      setSelectedJob(prefilledJob);
    }
  }, [prefilledJob, open]);

  const canAccessInviteFeature = useMemo(
    () => canAccess(FeatureType.JOB_INVITES_ENABLED),
    [canAccess]
  );

  const onCloseDialog = useCallback(() => {
    onClose();
    setSelectedJob(null);
    setNote(undefined);
    setView('selection');
    setJobSearchQuery('');
    setResults([]);
    setProgress(0);
    setError(null);
    setPage(0);
  }, [onClose]);

  const { value: buildPromptOpen, onTrue: openBuildPrompt, onFalse: closeBuildPrompt } = useBoolean();
  const [isCheckingProfiles, setIsCheckingProfiles] = useState(false);
  // Read before asking (JT1-1821): who has an empty profile, and the records already fetched, which
  // the run reuses.
  const [profileCheck, setProfileCheck] = useState<{ emptyIds: string[]; fetched: Map<string, IOrgCandidate> }>({
    emptyIds: [],
    fetched: new Map(),
  });

  const runBulkAdd = useCallback(async (buildFor: Set<string>, fetched: Map<string, IOrgCandidate>) => {
    closeBuildPrompt();
    if (!selectedJob || !candidates?.length) return;

    setView('processing');
    setProgress(0);
    setResults([]);
    setError(null);

    const newResults: Result[] = [];
    let successCount = 0;

    for (let i = 0; i < candidates.length; i++) {
      const candidateId = candidates[i];

      // eslint-disable-next-line no-await-in-loop
      const candidate = fetched.get(candidateId) ?? await getCandidateById(candidateId).unwrap();

      try {

        // eslint-disable-next-line no-await-in-loop
        await addCandidateToJob({
          job_id: selectedJob.id as string,
          candidate_id: candidate.id as string,
          create_invite: createInvite,
          default_stage: defaultStage,
          // Every application gets the note, but a mention notifies once: the first application to
          // be created carries the mentions, and the rest go without.
          note: note && { ...note, tagged_accounts: successCount === 0 ? note.tagged_accounts : [] },
          ...(buildFor.has(candidateId) && { build_profile: true }),
        }).unwrap();

        newResults.push({
          id: candidate.id,
          first_name: candidate.candidate_profile.first_name as string,
          last_name: candidate.candidate_profile.last_name as string,
          email: candidate.candidate_profile.email as string,
          status: 'success',
        });
        successCount++;
      } catch (e) {
        const errorMsg = generateAddCandidateToJobError(e);
        newResults.push({
          id: candidate.id,
          first_name: candidate.candidate_profile.first_name as string,
          last_name: candidate.candidate_profile.last_name as string,
          email: candidate.candidate_profile.email as string,
          status: 'error',
          error: errorMsg,
        });
      }

      setProgress(((i + 1) / candidates.length) * 100);
    }

    setResults(newResults);
    setView('results');

    const added = newResults.filter((r) => r.status === 'success').map((r) => r.id);
    if (added.length) onAdded?.(added, defaultStage);

    if (successCount === candidates.length) {
      enqueueSnackbar(
        t('candidates.invites.add_to_job_modal.api.success_bulk', {
          count: successCount,
        }),
        { variant: 'success' }
      );
    }
    else if (successCount === 0) {
      enqueueSnackbar(
        t('candidates.invites.add_to_job_modal.api.error_bulk', {
          count: candidates.length,
        }),
        { variant: 'error' }
      );
    }
    else {
      enqueueSnackbar(
        t('candidates.invites.add_to_job_modal.api.partial_success', {
          successCount,
          total: candidates.length,
        }),
        { variant: 'warning' }
      );
    }
  }, [
    addCandidateToJob,
    closeBuildPrompt,
    createInvite,
    defaultStage,
    note,
    enqueueSnackbar,
    candidates,
    selectedJob,
    t,
    getCandidateById,
    onAdded
  ]);

  // Straight into Screening skips the build that reaching Screening triggers, so ask once for the
  // whole batch (JT1-1821). Only profiles are checked: the backend skips anyone without a resume.
  const handleBulkAdd = useCallback(async () => {
    if (!selectedJob || !candidates?.length) return;

    if (defaultStage !== ApplicationStage.SCREENING) {
      runBulkAdd(new Set(), new Map());
      return;
    }

    setIsCheckingProfiles(true);
    const lookups = await Promise.allSettled(candidates.map((id) => getCandidateById(id, true).unwrap()));
    setIsCheckingProfiles(false);

    // A lookup that failed is fetched again in the run, and is not offered a build.
    const fetched = new Map<string, IOrgCandidate>();
    lookups.forEach((lookup, i) => {
      if (lookup.status === 'fulfilled') fetched.set(candidates[i], lookup.value);
    });
    const emptyIds = candidates.filter((id) => fetched.has(id) && !hasProfileContent(fetched.get(id)?.candidate_profile));

    if (!emptyIds.length) {
      runBulkAdd(new Set(), fetched);
      return;
    }

    setProfileCheck({ emptyIds, fetched });
    openBuildPrompt();
  }, [candidates, defaultStage, getCandidateById, openBuildPrompt, runBulkAdd, selectedJob]);

  const renderSelectionView = () => (
    <>
      <DialogTitle>
        {t('candidates.invites.add_to_job_modal.title_bulk')}
      </DialogTitle>
      <DialogContent>
        {!!error && <Alert severity="error">{error}</Alert>}
        <Grid container spacing={2} justifyContent="center" pt={1}>
          <Grid item xs={12}>
            {
              !prefilledJob && (
                <Alert variant="outlined" severity="info">
                  <Trans>
                    {t('candidates.invites.add_to_job_modal.alert_bulk', {
                      count: candidates.length,
                    })}
                  </Trans>
                </Alert>
              )
            }

            {
              prefilledJob && (
                <Alert variant="outlined" severity="info">
                  <Trans>
                    {t('candidates.invites.add_to_job_modal.prefilled_job_alert_bulk', {
                      count: candidates.length,
                      job_title: prefilledJob.title
                    })}
                  </Trans>
                </Alert>
              )
            }
          </Grid>

          {
            !prefilledJob && (
              <Grid item xs={12}>
                <Autocomplete
                  options={jobs?.results as IJob[] ?? []}
                  value={selectedJob}
                  disabled={!canAccessInviteFeature}
                  onInputChange={(_event, value) => setJobSearchQuery(value)}
                  onChange={(_event, value) => setSelectedJob(value)}
                  renderOption={({ key, ...props }, option: IJob) => (
                    <li key={key} {...props}>
                      <Stack direction="column">
                        <Typography variant="body1">{`${option.title}`}</Typography>
                        {tenantType === TenantType.Recruiter && (
                          <Typography variant="caption">
                            {option.client_company_name}
                          </Typography>
                        )}
                      </Stack>
                    </li>
                  )}
                  getOptionLabel={(option) => (option ? `${option.title}` : '')}
                  isOptionEqualToValue={(option: IJob, value: IJob) =>
                    option.id === value?.id
                  }
                  getOptionKey={(option) => (option as IJob).id as string}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('jobs.toolbar.search.placeholder')}
                    />
                  )}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
            )
          }

          <Grid item xs={12}>
            <Autocomplete
              options={ALLOWED_STAGES}
              value={defaultStage}
              onChange={(_event, value) =>
                setDefaultStage(value as ApplicationStage)
              }
              renderOption={({ key, ...props }, option: ApplicationStage) => (
                <li key={key} {...props}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${t(`enums.application_stage.${option}`)}`}
                    </Typography>
                  </Stack>
                </li>
              )}
              getOptionLabel={(option) =>
                option ? `${t(`enums.application_stage.${option}`)}` : ''
              }
              isOptionEqualToValue={(
                option: ApplicationStage,
                value: ApplicationStage
              ) => option === value}
              getOptionKey={(option) => (option as ApplicationStage)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    'candidates.invites.add_to_job_modal.default_stage.label'
                  )}
                />
              )}
              sx={{ minWidth: 200 }}
            />
          </Grid>

          <Grid item xs={12}>
            <AddToJobNoteField
              onChange={setNote}
              helperText={t('candidates.invites.add_to_job_modal.note.helper_bulk')}
            />
          </Grid>

          <Grid item xs={12} mb={5}>
            <FormControlLabel
              sx={{ pl: 1, alignItems: 'start' }}
              labelPlacement="end"
              label={
                <Stack direction="column">
                  <Typography variant="subtitle2">
                    {t(
                      'candidates.invites.add_to_job_modal.create_invite.label'
                    )}
                  </Typography>
                  <Typography variant="caption">
                    {t(
                      'candidates.invites.add_to_job_modal.create_invite.subtitle'
                    )}
                  </Typography>
                </Stack>
              }
              control={
                <Checkbox
                  checked={createInvite}
                  onChange={(e) => setCreateInvite(e.target.checked)}
                  color="primary"
                />
              }
            />
          </Grid>

          <Stack
            sx={{ py: 2, width: '100%' }}
            gap={1}
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button variant="text" onClick={onCloseDialog}>
              {t('common.cancel')}
            </Button>
            {canAccessInviteFeature ? (
              <LoadingButton
                type="submit"
                variant="contained"
                onClick={handleBulkAdd}
                disabled={
                  isSubmitting ||
                  isCheckingProfiles ||
                  !selectedJob ||
                  !candidates?.length
                }
                loading={isSubmitting || isCheckingProfiles}
              >
                {t('candidates.invites.add_to_job_modal.action_bulk', {
                  count: candidates.length,
                })}
              </LoadingButton>
            ) : (
              <FeatureLockedChip text={t('common.upgrade')} />
            )}
          </Stack>
        </Grid>
      </DialogContent>
    </>
  );

  const renderProcessingView = () => (
    <>
      <DialogTitle>{t('candidates.invites.add_to_job_modal.title_bulk')}</DialogTitle>
      <DialogContent>
        <Stack alignItems="center" spacing={2} py={4}>

          <CircularProgress variant="determinate" value={progress} />

          <Stack alignItems="center">
            <Typography variant="subtitle1">
              {t('candidates.invites.add_to_job_modal.processing_title', {
                count: candidates.length
              })}
            </Typography>
            <Typography variant="body2">
              {t('candidates.invites.add_to_job_modal.processing_progress', {
                current: Math.round(progress),
              })}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );

  const renderResultsView = () => {
    const paginatedResults = results.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <DialogTitle>{t('candidates.invites.add_to_job_modal.results')}</DialogTitle>
        <DialogContent>

          {/* overview of results */}
          <Stack spacing={2} mb={2}>
            <Alert severity="info">
              <Trans
                i18nKey="candidates.invites.add_to_job_modal.results_summary_bulk"
                values={{ successCount: results.filter(r => r.status === 'success').length, total: results.length }}
                components={{ strong: <strong /> }}
              />
            </Alert>
          </Stack>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.candidate')}</TableCell>
                <TableCell>{t('common.error')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result) => (
                <TableRow key={result.id}>
                  <TableCell>
                    <Stack direction="row" alignItems="start" gap={1}>

                      {result.status === 'success'
                        ? <Iconify icon="icon-park-twotone:check-one" color="success.main" mt={1} />
                        : <Iconify icon="icon-park-twotone:close-one" color="error.main" mt={1} />}

                      <Stack direction="column">
                        <Typography variant="subtitle2">
                          {`${result.first_name} ${result.last_name}`}
                        </Typography>
                        <Typography variant="caption">
                          {result.email}
                        </Typography>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>

                    <Typography variant="body2">
                      {result.error || '-'}
                    </Typography>

                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={results.length}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
          <Stack
            sx={{ py: 2, width: '100%' }}
            gap={1}
            flexDirection="row"
            justifyContent="flex-end"
          >
            <Button variant="contained" onClick={onCloseDialog}>
              {t('common.close')}
            </Button>
          </Stack>
        </DialogContent>
      </>
    );
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={view === 'results' ? onCloseDialog : undefined}>
      {view === 'selection' && renderSelectionView()}
      {view === 'processing' && renderProcessingView()}
      {view === 'results' && renderResultsView()}

      <BuildProfilePrompt
        open={buildPromptOpen}
        count={profileCheck.emptyIds.length}
        onBuild={() => runBulkAdd(new Set(profileCheck.emptyIds), profileCheck.fetched)}
        onSkip={() => runBulkAdd(new Set(), profileCheck.fetched)}
        onClose={closeBuildPrompt}
      />
    </Dialog>
  );
}